import { mount } from 'svelte';
import App from './App.svelte';
// Monochrome emoji in the type's own weight for event titles (--title-font), in
// place of the platform's colour set; only the subsets a page draws are fetched.
import '../styles/title-emoji.css';
// IBM Plex, self-hosted: Sans for the UI, Mono for --mono, Serif italic for
// calendar names. Each subset loads only once a page draws its characters.
import '@fontsource-variable/ibm-plex-sans/wght.css';
import '@fontsource-variable/ibm-plex-sans/wght-italic.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-serif/400-italic.css';
import { registerSW } from 'virtual:pwa-register';
import { swStatus, watchSwUpdates, scheduleSwUpdateChecks, setAppUpdater, markControllerChanged } from './lib/sw-status.svelte';
import { viewport } from './lib/viewport.svelte';

// Always open on today: stop the browser from restoring the timeline's prior
// scroll position on reload, which otherwise overrides the center-on-today pass.
if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// Recover from a failed dynamic import — a chunk renamed by a new deploy while a
// stale page / service worker still references the old name would otherwise blank
// the app. Reload once to pull the fresh index + chunks; a short cooldown in
// sessionStorage prevents a reload loop if the chunk is genuinely unreachable.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', () => {
    let allow = false;
    try {
      const KEY = 'kalendes:preload-reload-at';
      const last = Number(sessionStorage.getItem(KEY) ?? '0');
      if (Date.now() - last >= 10_000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        allow = true;
      }
    } catch {
      // No sessionStorage (private mode) — skip the auto-reload rather than risk a loop.
    }
    if (allow) location.reload();
  });
}

const target = document.getElementById('app');
if (!target) throw new Error('No #app element');

// The first-paint ghost UI (index.html) is a fixed overlay, so the real app can
// mount and paint *underneath* it, then the overlay cross-fades out to reveal it —
// no layout shift (the app is in flow below the fixed skeleton the whole time).
const skeleton = document.getElementById('app-skeleton');

mount(App, { target });

if (skeleton) {
  if (viewport.prefersReducedMotion) {
    skeleton.remove();
  } else {
    // Double rAF: let the freshly-mounted app paint one frame underneath before
    // the overlay starts fading, so nothing shows through mid-transition.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        skeleton.addEventListener('transitionend', () => skeleton.remove(), { once: true });
        // Fallback if transitionend never fires (e.g. the tab is backgrounded).
        setTimeout(() => skeleton.remove(), 600);
        skeleton.classList.add('is-hiding');
      }),
    );
  }
}

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  // A new worker asks on install whether this page will tell it when to take
  // over; answering keeps it waiting for a safe moment (public/sw-bridge.js).
  navigator.serviceWorker.addEventListener('message', (e) => {
    if (e.data?.type === 'kalendes:sw-ping') (e.source as ServiceWorker | null)?.postMessage({ type: 'kalendes:sw-pong' });
  });
  // If a new worker took over anyway (this tab was frozen and didn't answer),
  // this page's code is stale: reload too, at the same safe moment.
  const hadController = navigator.serviceWorker.controller != null;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) markControllerChanged();
  });
}

if (import.meta.env.PROD) {
  const updateSW = registerSW({
    immediate: true,
    // A new version is installed and waiting: App applies it (reload) once the
    // settings and event editor are closed.
    onNeedRefresh() {
      swStatus.updateReady = true;
    },
    // First install finished precaching the shell — let the tray flash a brief
    // "offline ready" confirmation.
    onOfflineReady() {
      swStatus.offlineReady = true;
    },
    // A new version installing over the running one: the status chip's dot
    // pulses amber until it takes over.
    onRegisteredSW(_url, reg) {
      if (!reg) return;
      watchSwUpdates(reg, navigator.serviceWorker);
      // Keep looking for new versions while the app stays open.
      scheduleSwUpdateChecks(reg);
    },
  });
  setAppUpdater(() => updateSW(true));
}
